/**
 * Offline outbox for the onsite app.
 *
 * Field engineers lose connectivity inside customer sites. Daily reports
 * and site intel are captured locally and reconciled on reconnect.
 *
 * Storage: IndexedDB (object store `outbox`). Each entry is a JSON
 * envelope describing the intended Supabase mutation. On reconnect,
 * `replayOutbox()` drains the queue.
 *
 * Conflict policy:
 *   - daily_reports: last-write-wins by (trip_id, report_date)
 *   - site_intel: append-only insert
 *
 * Retry policy: an entry is retried at most OUTBOX_MAX_ATTEMPTS times. After
 * that it stays in the store with `lastError` so the indicator can show it
 * and the technician can retry (resets the counter) or discard it. Without a
 * cap a permanent failure (RLS, validation) would be retried forever and
 * silently.
 *
 * This module is intentionally framework-free so it can be invoked
 * from any onsite call site, and re-used in a service worker later.
 */

import { supabase } from './supabase';
import { errorMessage } from './errors';

const DB_NAME = 'onsite-outbox';
const DB_VERSION = 1;
const STORE = 'outbox';

export const OUTBOX_MAX_ATTEMPTS = 5;

export type OutboxKind = 'daily_report_upsert' | 'site_intel_insert';

export interface OutboxEntry {
  /** Local id for dedupe in the UI list */
  id: string;
  /** Wall-clock when the mutation was attempted */
  queuedAt: string;
  /** Mutation kind drives the replay strategy */
  kind: OutboxKind;
  /** Supabase row payload */
  payload: Record<string, unknown>;
  /** Number of replay attempts so far */
  attempts: number;
  /** Last error message, if any */
  lastError?: string;
  /** Wall-clock of the last failed attempt */
  lastAttemptAt?: string;
}

export type ReplayResult =
  | { ok: true; entry: OutboxEntry }
  | { ok: false; entry: OutboxEntry; error: string; skipped: boolean };

export function isExhausted(entry: OutboxEntry): boolean {
  return entry.attempts >= OUTBOX_MAX_ATTEMPTS;
}

export function describeOutboxKind(kind: OutboxKind): string {
  switch (kind) {
    case 'daily_report_upsert':
      return 'Daily report';
    case 'site_intel_insert':
      return 'Site intel observation';
    default:
      return kind;
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function genId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

async function putEntry(entry: OutboxEntry): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getEntry(id: string): Promise<OutboxEntry | null> {
  const db = await openDb();
  return new Promise<OutboxEntry | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => resolve((req.result as OutboxEntry | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueue(kind: OutboxKind, payload: Record<string, unknown>): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    id: genId(),
    queuedAt: new Date().toISOString(),
    kind,
    payload,
    attempts: 0,
  };
  await putEntry(entry);
  notify();
  return entry;
}

export async function listEntries(): Promise<OutboxEntry[]> {
  const db = await openDb();
  return new Promise<OutboxEntry[]>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const entries = ((req.result as OutboxEntry[]) ?? []).slice();
      entries.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
      resolve(entries);
    };
    req.onerror = () => reject(req.error);
  });
}

async function removeEntry(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  notify();
}

/** Drop a queued mutation for good (the user chose to abandon it). */
export async function discardEntry(id: string): Promise<void> {
  await removeEntry(id);
}

/** Reset the attempt counter on an exhausted entry and replay immediately. */
export async function retryEntry(id: string): Promise<ReplayResult | null> {
  const entry = await getEntry(id);
  if (!entry) return null;
  const reset: OutboxEntry = { ...entry, attempts: 0, lastError: undefined, lastAttemptAt: undefined };
  await putEntry(reset);
  notify();
  return replayOne(reset);
}

async function bumpFailure(entry: OutboxEntry, error: string): Promise<OutboxEntry> {
  const next: OutboxEntry = {
    ...entry,
    attempts: entry.attempts + 1,
    lastError: error,
    lastAttemptAt: new Date().toISOString(),
  };
  await putEntry(next);
  notify();
  return next;
}

async function replayOne(entry: OutboxEntry): Promise<ReplayResult> {
  if (isExhausted(entry)) {
    return { ok: false, entry, error: entry.lastError ?? 'Retry limit reached', skipped: true };
  }
  try {
    if (entry.kind === 'daily_report_upsert') {
      const { error } = await supabase
        .from('daily_reports')
        .upsert(entry.payload as never, { onConflict: 'trip_id,report_date' });
      if (error) throw error;
    } else if (entry.kind === 'site_intel_insert') {
      const { error } = await supabase.from('site_intel').insert(entry.payload as never);
      if (error) throw error;
    } else {
      throw new Error(`Unknown outbox entry kind: ${(entry as { kind?: string }).kind}`);
    }
    await removeEntry(entry.id);
    return { ok: true, entry };
  } catch (e) {
    const msg = errorMessage(e);
    const next = await bumpFailure(entry, msg);
    return { ok: false, entry: next, error: msg, skipped: false };
  }
}

let replayInFlight: Promise<ReplayResult[]> | null = null;

export async function replayOutbox(): Promise<ReplayResult[]> {
  if (replayInFlight) return replayInFlight;
  replayInFlight = (async () => {
    const entries = await listEntries();
    const results: ReplayResult[] = [];
    for (const e of entries) {
      results.push(await replayOne(e));
    }
    return results;
  })();
  try {
    return await replayInFlight;
  } finally {
    replayInFlight = null;
  }
}

// ─── Pub/sub for the React indicator ────────────────────────────────
const listeners = new Set<() => void>();

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const l of listeners) l();
}

// Replay automatically whenever the browser comes back online.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void replayOutbox();
  });
}
